import { MpBreadcrumbFilterData } from '@mp-react/table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SidebarDark from '../../../common/SidebarDark/SidebarDark';
import useStyles from './OrganizationUnits.styles';
import UnitItem from './UnitItem';

interface Props {
  tree?: MpBreadcrumbFilterData;
  allSelectionId?: string;
  onClick: (id: string, name: string) => void;
  selectedId?: string;
  disabled: boolean;
}

export default function OrganizationUnits({
  tree,
  allSelectionId,
  onClick,
  selectedId,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const ungroupedCompanies = useMemo(
    () =>
      (tree?.company_groups as MpBreadcrumbFilterData[])?.filter(
        (company) => company.id === 'ungrouped',
      )?.[0],
    [tree?.company_groups],
  );

  const filteredOutUngroupedCompanies = useMemo(
    () =>
      (tree?.company_groups as MpBreadcrumbFilterData[])?.filter(
        (company) => company.id !== 'ungrouped',
      ),
    [tree?.company_groups],
  );

  const UngroupedCompaniesList = useMemo(() => {
    return (
      !!ungroupedCompanies && (
        <SidebarDark>
          {(ungroupedCompanies.companies as MpBreadcrumbFilterData[]).map(
            (company: MpBreadcrumbFilterData) => (
              <UnitItem
                id={company.id as string}
                key={company.id}
                label={company.name as string}
                onClick={() =>
                  onClick(`filter[companyId]=${company.id}`, company.name ?? '')
                }
                disabled={disabled}
                isActive={selectedId === `filter[companyId]=${company.id}`}
                className={classes.companyListItemRoot}
              />
            ),
          )}
        </SidebarDark>
      )
    );
  }, [
    classes.companyListItemRoot,
    disabled,
    onClick,
    selectedId,
    ungroupedCompanies,
  ]);

  const itemMap = useCallback(
    (items: MpBreadcrumbFilterData[]) => {
      return items.map((item) => {
        return (
          <UnitItem
            id={item.id as string}
            key={item.id}
            label={item.name as string}
            onClick={() =>
              onClick(`filter[companyGroupId]=${item.id}`, item.name ?? '')
            }
            isActive={selectedId === `filter[companyGroupId]=${item.id}`}
            disabled={disabled}
          >
            {!!item?.companies && (
              <SidebarDark>
                {(item.companies as MpBreadcrumbFilterData[]).map(
                  (company: MpBreadcrumbFilterData) => (
                    <UnitItem
                      id={company.id as string}
                      key={company.id}
                      label={company.name as string}
                      onClick={() =>
                        onClick(
                          `filter[companyId]=${company.id}`,
                          company.name ?? '',
                        )
                      }
                      disabled={disabled}
                      isActive={
                        selectedId === `filter[companyId]=${company.id}`
                      }
                      className={classes.companyListItemRoot}
                    />
                  ),
                )}
              </SidebarDark>
            )}
          </UnitItem>
        );
      });
    },
    [classes.companyListItemRoot, disabled, onClick, selectedId],
  );

  return (
    <SidebarDark>
      {!!allSelectionId && (
        <UnitItem
          onClick={(id) => onClick(id, t('common.all'))}
          id={allSelectionId}
          label={t('common.all')}
          isActive={selectedId === allSelectionId}
          disabled={disabled}
          className={classes.selectedWithoutBg}
        >
          {UngroupedCompaniesList}
        </UnitItem>
      )}
      {!!tree?.company_groups &&
        itemMap(filteredOutUngroupedCompanies as MpBreadcrumbFilterData[])}
    </SidebarDark>
  );
}
